import { Button, Divider, Form, Modal, Select } from "antd";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";
import type { PromotionRelegationRuleTableRow } from "~Features/EditSeason/components/PromotionRelegationRules/types/promotionRelegationRuleTableRow.ts";
import { FormInput } from "~components/Form/FormInput";
import { FormSelect } from "~components/Form/FormSelect";
import { PromotionRelegationRuleTypes } from "~enums/promotionRelegationRuleTypes.ts";
import type { PromotionRelegationRule } from "~types/promotionRelegationRule.ts";
import "./UpsertPromotionRelegationRulesModal.scss";
type Props = {
  isOpen: boolean;
  onCancel: () => void;
  loading: boolean;
  onOk: (data: PromotionRelegationRule) => Promise<void>;
  rule?: PromotionRelegationRuleTableRow;
};

export const UpsertPromotionRelegationRulesModal = ({ isOpen, onOk, onCancel, loading, rule }: Props) => {
  const [form] = Form.useForm<PromotionRelegationRule>();
  const { formatMessage } = useIntl();

  const fields = [
    {
      titleIntlKey: "promotion.relegation.rules.ruleType",
      descriptionIntlKey: "form.help.promotion.relegation.rules.ruleType",
    },
    {
      titleIntlKey: "promotion.relegation.rules.position",
      descriptionIntlKey: "form.help.promotion.relegation.rules.position",
    },
    {
      titleIntlKey: "promotion.relegation.rules.value",
      descriptionIntlKey: "form.help.promotion.relegation.rules.value",
    },
  ];

  const [isHelpSectionOpen, setIsHelpSectionOpen] = useState<boolean>(false);

  const onSave = async () => {
    const dataSource = (await form.validateFields()) ?? {};
    await onOk(dataSource);
    form.resetFields();
  };

  return (
    <>
      {isHelpSectionOpen ? (
        <Modal
          open={isHelpSectionOpen}
          onCancel={() => setIsHelpSectionOpen(false)}
          title={
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {formatMessage({
                id: "promotion.relegation.rules.modal.upsert.title",
              })}
            </div>
          }
          okButtonProps={{ loading, hidden: true }}
          cancelButtonProps={{ hidden: true }}
        >
          <div>{formatMessage({ id: "details" })}</div>
          <Divider style={{ margin: "1px 0", borderColor: "#DADADA" }} />
          {fields.map((field) => (
            <Fragment key={field.titleIntlKey}>
              <div className="help-label">{formatMessage({ id: `${field.titleIntlKey}` })}</div>
              <span className="help-description">{formatMessage({ id: `${field.descriptionIntlKey}` })}</span>
            </Fragment>
          ))}
        </Modal>
      ) : (
        <Modal
          open={isOpen}
          onCancel={onCancel}
          title={
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {formatMessage({
                id: "promotion.relegation.rules.modal.upsert.title",
              })}
            </div>
          }
          okButtonProps={{ loading }}
          cancelButtonProps={{ disabled: loading }}
          cancelText={formatMessage({
            id: "cancel",
          })}
          onOk={onSave}
        >
          <div className="details-container">
            {formatMessage({ id: "details" })}
            <Button
              icon={<i className="fa-regular fa-circle-question" />}
              type="text"
              size="small"
              onClick={() => setIsHelpSectionOpen(true)}
            />
          </div>

          <Divider style={{ margin: "1px 0", borderColor: "#DADADA" }} />
          <Form
            initialValues={{
              ruleType: PromotionRelegationRuleTypes.PROMOTION,
              ...rule,
            }}
            form={form}
            className="upsert-promotion-relegation-rules-modal-form"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            style={{ marginTop: "20px" }}
          >
            <FormSelect<PromotionRelegationRule>
              label={formatMessage({ id: "promotion.relegation.rules.ruleType" })}
              name="ruleType"
              rules={[
                {
                  required: true,
                  message: formatMessage(
                    {
                      id: "form.help.required.field",
                    },
                    {
                      field: formatMessage({
                        id: "promotion.relegation.rules.ruleType",
                      }),
                    },
                  ),
                },
              ]}
            >
              {Object.values(PromotionRelegationRuleTypes).map((type) => (
                <Select.Option key={type} value={type}>
                  {type}
                </Select.Option>
              ))}
            </FormSelect>
            <FormInput<PromotionRelegationRule>
              label={formatMessage({ id: "promotion.relegation.rules.position" })}
              name="position"
              inputProps={{ placeholder: "1", type: "number" }}
              getValueFromEvent={(event) => {
                const value = event.target.value;
                return value ? Number(value) : undefined;
              }}
              rules={[
                {
                  required: true,
                  message: formatMessage(
                    {
                      id: "form.help.required.field",
                    },
                    {
                      field: formatMessage({
                        id: "promotion.relegation.rules.position",
                      }),
                    },
                  ),
                },
              ]}
            />
            <FormInput<PromotionRelegationRule>
              label={formatMessage({ id: "promotion.relegation.rules.value" })}
              name="value"
              inputProps={{ placeholder: "Div2" }}
              rules={[
                {
                  required: true,
                  message: formatMessage(
                    {
                      id: "form.help.required.field",
                    },
                    {
                      field: formatMessage({
                        id: "promotion.relegation.rules.value",
                      }),
                    },
                  ),
                },
              ]}
            />
          </Form>
        </Modal>
      )}
    </>
  );
};
